import { api } from '@packages/backend/convex/_generated/api';
import {
  byNewestFirst,
  getLinkedMenuItemCount,
  getLinkedMenuItemNames,
  type ModifierGroup,
  modifierGroupSchema,
  sortByDisplayIndex,
} from '@packages/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from 'convex-helpers/react';
import { Settings } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ActionButton } from '@/components/ui+/ActionButton';
import { LoadingIcon } from '@/components/ui+/LoadingIcon';
import { ModifierGroupDialog } from './modifiers/components/ModifierGroupDialog';
import { ModifierGroupForm } from './modifiers/components/ModifierGroupForm';
import { ModifiersRearrangeList } from './modifiers/components/ModifiersRearrangeList';
import { ModifiersTable } from './modifiers/components/ModifiersTable';

export const Route = createFileRoute('/r/$restaurantId/menu/modifiers')({
  component: Component,
});

function Component() {
  const { restaurantId } = Route.useParams();
  const {
    data: menu,
    isError,
    isPending,
  } = useQuery(api.menus.getByRestaurantId, { restaurantId });

  const [newModifierGroup, setNewModifierGroup] = useState<ModifierGroup | null>(null);
  // keep track of id rather than boolean value, as there is many mod groups on the one page
  const [rearrangingGroupId, setRearrangingGroupId] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<'direct' | 'shared'>('shared');

  if (isPending) {
    return <LoadingIcon />;
  }
  if (isError) return null; // Handled by parent component
  if (menu === null) {
    return null; // Handled by menu items tab
  }

  const sortedModGroups = [...menu.modifierGroups].sort(byNewestFirst).filter((modifierGroup) => {
    if (filterType === 'shared') return modifierGroup.isShared;
    if (filterType === 'direct') return !modifierGroup.isShared;
    return true;
  });

  if (menu.categories.length === 0) {
    return (
      <div className={'bg-white p-4 text-center border'}>
        <div>Add some categories first.</div>
      </div>
    );
  }

  const dismissNewModifierDialog = () => {
    setNewModifierGroup(null);
  };

  return (
    <div className={'xl:w-2/3 grid gap-8'}>
      <div className="flex items-center gap-1">
        <Tabs
          onValueChange={(value) => setFilterType(value === 'direct' ? 'direct' : 'shared')}
          value={filterType}
        >
          <TabsList>
            <TabsTrigger value="shared">Shared</TabsTrigger>
            <TabsTrigger value="direct">Item specific</TabsTrigger>
          </TabsList>
        </Tabs>
        <ActionButton
          disabled={filterType !== 'shared'}
          disabledTooltipPosition={'bottom'}
          disabledTooltipText={'Create item specific modifiers via "Menu items" tab'}
          onClick={() => setNewModifierGroup(modifierGroupSchema.decode({ name: '' }))}
          size="sm"
          variant="link"
        >
          Create new modifier group
        </ActionButton>
      </div>

      {sortedModGroups.map((modifierGroup) => {
        const linkedMenuItemNames = getLinkedMenuItemNames({
          menuItems: menu.menuItems,
          modifierGroupUuid: modifierGroup.uuid,
        });

        return (
          <div key={modifierGroup.uuid}>
            {rearrangingGroupId !== modifierGroup.uuid && (
              <div className="flex justify-between items-center">
                <div className={'flex gap-2 items-center'}>
                  <h3 className="text-xl leading-7 font-medium sm:truncate sm:text-2xl sm:tracking-tight mb-2 ">
                    {modifierGroup.name}
                  </h3>
                  <ModifierGroupDialog
                    linkedMenuItemsCount={getLinkedMenuItemCount({
                      menuItems: menu.menuItems,
                      modifierGroupUuid: modifierGroup.uuid,
                    })}
                    menuId={menu._id}
                    modifierGroup={modifierGroup}
                    modifiers={menu.modifiers.filter(
                      (m) => m.modifierGroupUuid === modifierGroup.uuid,
                    )}
                    triggerButton={
                      <ActionButton className={'h-8 mt-0.5 mb-1'} size={'sm'} variant="outline">
                        <Settings size={16} />
                      </ActionButton>
                    }
                  />
                  <div className={'text-xs text-muted-foreground'}>
                    {linkedMenuItemNames.length === 0 && '(not linked)'}
                    {linkedMenuItemNames.length > 0 &&
                      linkedMenuItemNames.length <= 3 &&
                      `Linked to ${linkedMenuItemNames.join(', ')}`}
                    {linkedMenuItemNames.length > 3 && (
                      <Popover>
                        <PopoverTrigger asChild>
                          <ActionButton
                            className="h-auto p-0 text-xs text-muted-foreground"
                            variant="link"
                          >
                            Linked to {linkedMenuItemNames.length} items...
                          </ActionButton>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-4 py-2">
                          <ul>
                            {linkedMenuItemNames.map((item, index) => (
                              <li className="text-sm" key={item}>
                                {item}
                              </li>
                            ))}
                          </ul>
                        </PopoverContent>
                      </Popover>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {modifierGroup.qtyMax > 1 && <Badge variant={'outline'}>Multi-choice</Badge>}
                  {modifierGroup.modifierType === 'SIZE' && (
                    <Badge variant={'outline'}>Size Group</Badge>
                  )}
                </div>
              </div>
            )}

            {rearrangingGroupId === modifierGroup.uuid ? (
              <ModifiersRearrangeList
                menuId={menu._id}
                modifierGroupName={modifierGroup.name}
                modifiers={sortByDisplayIndex(
                  menu.modifiers.filter((m) => m.modifierGroupUuid === modifierGroup.uuid),
                )}
                onRearrangingDone={() => setRearrangingGroupId(null)}
              />
            ) : (
              <ModifiersTable
                menuId={menu._id}
                modifierGroup={modifierGroup}
                modifiers={sortByDisplayIndex(
                  menu.modifiers.filter((m) => m.modifierGroupUuid === modifierGroup.uuid),
                )}
                onRearrange={(modifierGroupId) => setRearrangingGroupId(modifierGroupId)}
              />
            )}
          </div>
        );
      })}

      {newModifierGroup && (
        <Dialog
          onOpenChange={(open) => (!open ? dismissNewModifierDialog() : undefined)}
          open={!!newModifierGroup}
        >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>New modifier group</DialogTitle>
              <DialogDescription />
            </DialogHeader>
            <ModifierGroupForm
              hasLinkedMenuItems={false}
              isNew={true}
              linkedMenuItemsCount={0}
              menuId={menu._id}
              modifierGroup={newModifierGroup}
              modifiers={[]}
              onDismiss={dismissNewModifierDialog}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
